import { Injectable, OnDestroy } from '@angular/core'
import { AuthTokenService } from '@helios/auth'
import { TelemetryService } from '@helios/telemetry'
import { differenceInSeconds } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { filter, map, takeUntil } from 'rxjs/operators'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'
import { EventType, MessagingService } from '../../messaging.service'
import { HeliosMessage, HeliosMessageType } from '../../models/messaging.model'
import { HeliosEventFilterCriteria } from '../../models/websocket-filter.model'

const SOCKET_RETRY_COUNT = 10
const SOCKET_RETRY_TIMEOUT = 1000
export const IDLE_TIMEOUT_SECONDS = 15
const IDLE_CHECK_INTERVAL_MILLIS = 1000

@Injectable({
  providedIn: 'root',
})
export class WebSocketsService extends MessagingService implements OnDestroy {
  disconnectSubject$ = new Subject<void>()
  readonly isConnected$ = new ReplaySubject<boolean>(1)
  readonly message$ = new Subject<unknown>()
  private reconnectAttempts = 0
  /**
   * This one is for metrics. It is not reset on reconnect.
   * @private
   */
  private totalReconnectAttempts = 0
  private isSocketReady = false

  /**
   * @internal
   */
  timeOfLastMessage: Date | undefined
  /** @internal */
  connectionUrl: string | undefined

  private readonly webSocketFilters: Map<string, HeliosEventFilterCriteria[]> =
    new Map()

  /**
   * @internal
   */
  readonly idleInterval = setInterval(() => {
    if (this.isIdleSocket() && this.connectionUrl) {
      console.warn(
        `Detected an idle websocket: ${this.connectionUrl}.  Reconnecting`,
      )
      this.connect(this.connectionUrl)
    }
  }, IDLE_CHECK_INTERVAL_MILLIS)

  socket$: WebSocketSubject<HeliosMessage<unknown>> | undefined

  constructor(
    private readonly telemetry: TelemetryService,
    private readonly auth: AuthTokenService,
  ) {
    super()
  }

  /**
   * Used for logging metrics to FullStory
   * @internal
   */
  getWSType(): 'GLOBAL' | 'SESSION' | 'OTHER' {
    if (this.connectionUrl == null) {
      return 'OTHER'
    }

    if (this.connectionUrl.toLowerCase().endsWith('global/')) {
      return 'GLOBAL'
    }

    if (this.connectionUrl.toLowerCase().includes('session')) {
      return 'SESSION'
    }

    return 'OTHER'
  }

  /**
   * Connect to the server
   */
  connect(url: string): void {
    this.disconnect()
    this.connectionUrl = url
    this.disconnectSubject$ = new Subject<void>()
    this.attachSocket(url)
  }

  /**
   * Explicitly disconnect from the server
   */
  disconnect(): void {
    this.connectionUrl = undefined
    this.timeOfLastMessage = undefined
    this.isSocketReady = false
    this.disconnectSubject$.next()
  }

  /**
   * Send a message to the server
   */
  send<T>(message: T): void {
    this.socket$?.next(message as unknown as HeliosMessage<unknown>)
  }

  /**
   * Sets the Web Socket filters for a given key & then sends them.
   *
   * Each grouping of filters, grouped by key, is OR'd. Each filter within filters is cross applied and then AND'd.
   *
   * Example:
   * {key: 'txList', filters: [{jsonPath: 'payload/accountId', values: ['1', '2']}, {jsonPath: 'payload/securityId', values: ['3', '4']}]}
   * {key: 'posList', filters: [{jsonPath: 'payload/accountId', values: ['5']}, {jsonPath: 'payload/securityId', values: ['6', '7]}]}
   * The backend will return events that match any of the following:
   * If no security id and no account id then it will let the event though (no attribute at the jsonPath seems to be treated as a wild card
   * If no security id but there is an account id, then accountId in [1, 2] OR accountId in [5]
   * If no account id but there is a security id, then securityId in [3, 4] OR securityId in [6, 7]
   * If both are present then (accountId in [1,2] AND securityId in [3,4]) OR (accountId in [5] AND securityId in [6, 7]
   *
   * If the filters for the given key are identical to the currently stored filters, the call is a no-op.
   *
   * @param key
   * @param filters
   */
  setFilters(key: string, filters: HeliosEventFilterCriteria[]): void {
    const existing = this.webSocketFilters.get(key)
    if (existing && isEqual(existing, filters)) {
      return
    }

    const isReplacement = this.webSocketFilters.has(key)
    this.webSocketFilters.set(key, filters)

    if (!this.isSocketReady) {
      return
    }

    if (isReplacement) {
      this.sendAllFilters()
    } else {
      this.sendFilters(filters)
    }
  }

  /**
   * Deletes the Web Socket filters related to the given key.
   *
   * @param key
   */
  deleteFilters(key: string): void {
    if (!this.webSocketFilters.has(key)) {
      return
    }

    this.webSocketFilters.delete(key)

    if (!this.isSocketReady) {
      return
    }

    this.sendAllFilters()
  }

  /**
   * Sends all the filters
   *
   * The filters are stored additively. Ie, when we send a new filter all prior filters are still applied.
   * Due to this, we need to clear the filters first.
   *
   * @private
   */
  private sendAllFilters(): void {
    this.send({
      eventType: HeliosMessageType.ClearFilters,
      payload: {},
    })

    for (const filterCriteria of this.webSocketFilters.values()) {
      this.sendFilters(filterCriteria)
    }
  }

  /**
   * Sends the provided filters. All prior filters will still be applied.
   *
   * @param filters
   * @private
   */
  private sendFilters(filters: HeliosEventFilterCriteria[]): void {
    this.send({
      eventType: HeliosMessageType.AddFilters,
      payload: {
        filterCriteria: filters,
      },
    })
  }

  /**
   * Listen to a specific event type
   */
  on<K, T extends HeliosMessage<K>>(
    type: EventType,
    filters?: HeliosMessageType | HeliosMessageType[],
  ): Observable<T> {
    switch (type) {
      case EventType.MESSAGE:
        return this.message$.pipe(
          map(e => {
            if (e === 'Connection Authenticated') {
              return null as unknown as T
            }

            return e as T
          }),
          filter(e => {
            if (filters == null) {
              return true
            }

            if (Array.isArray(filters)) {
              return filters.includes(e.eventType)
            }

            return filters === e.eventType
          }),
        )
      case EventType.CONNECTED:
        return this.isConnected$.pipe(
          map(value => ({ payload: value } as unknown as T)),
        )
    }
  }

  /**
   * Create a new instance of a socket and signal when it is connected
   *
   * @internal
   */
  attachSocket(url: string) {
    this.socket$ = webSocket({
      url,
      // They come in as strings, so we need to parse these messages
      deserializer: e => {
        try {
          this.timeOfLastMessage = new Date()
          const data = JSON.parse(e.data)
          return data as HeliosMessage<unknown>
        } catch (err) {
          return {
            payload: e.data,
          } as HeliosMessage<unknown>
        }
      },
      // Signal that we are connected
      openObserver: {
        next: () => {
          this.isConnected$.next(true)
          this.getAuthMessage()
            .then(msg => {
              this.send(msg)
              // Gate that prevents setFilters/deleteFilters from sending
              // messages before the auth handshake completes. Any filters
              // registered while this is false are stored in webSocketFilters
              // but not sent — sendAllFilters() below picks them up once auth succeeds.
              this.isSocketReady = true
              this.reconnectAttempts = 0
              if (this.webSocketFilters.size > 0) {
                this.sendAllFilters()
              }
            })
            .catch(err => {
              console.error('Failed to authenticate WebSocket', err)
              this.isConnected$.next(false)
              this.retryConnection(err)
            })
        },
      },
      closeObserver: {
        next: () => {
          this.isSocketReady = false
          this.isConnected$.next(false)
        },
      },
    })

    this.socket$?.pipe(takeUntil(this.disconnectSubject$)).subscribe(
      message => this.message$.next(message),
      // Signal something went wrong and we are no longer connected
      error => {
        console.error('WebSocket error', error)
        this.isSocketReady = false
        this.isConnected$.next(false)
        this.retryConnection(error)
      },
      // Signal that we are no longer connected as a fallback to the closeObserver
      () => {
        this.isSocketReady = false
        this.isConnected$.next(false)
        if (this.connectionUrl) {
          this.retryConnection({ reason: 'Server closed connection' })
        }
      },
    )
  }

  /**
   * Check if a websocket connection has gone idle
   */
  isIdleSocket(): boolean {
    // We should receive a heartbeat every 10 seconds
    return this.getSecondsSinceLastMessage() > IDLE_TIMEOUT_SECONDS
  }

  private getSecondsSinceLastMessage(): number {
    const now = new Date()

    return differenceInSeconds(now, this.timeOfLastMessage ?? now)
  }

  /**
   * @internal
   */
  private retryConnection(error: unknown): void {
    if (this.reconnectAttempts > SOCKET_RETRY_COUNT) {
      console.error(
        `Socket disconnected, exceeded max reconnect attempts (${SOCKET_RETRY_COUNT})`,
      )
      return
    }

    if (!this.connectionUrl) {
      console.error('Cannot reconnect: connectionUrl is undefined')
      return
    }

    this.reconnectAttempts++
    this.totalReconnectAttempts++
    console.warn(
      `Socket for connectionUrl ${this.connectionUrl} disconnected, will attempt ${this.reconnectAttempts} in ${SOCKET_RETRY_TIMEOUT}ms`,
      error,
    )
    this.telemetry.emitCustomEvent('heliosWebSocketReconnect', {
      connectionUrl: this.connectionUrl,
      urlType: this.getWSType(),
      reconnectAttempts: this.reconnectAttempts,
      totalReconnectAttempts: this.totalReconnectAttempts,
      secondsSinceLastMessage: this.getSecondsSinceLastMessage(),
    })

    setTimeout(() => {
      if (this.connectionUrl) {
        this.connect(this.connectionUrl)
      }
    }, SOCKET_RETRY_TIMEOUT)
  }

  /**
   * Remove the interval checking for an idle connection when this service is destroyed
   */
  ngOnDestroy() {
    this.disconnect()
    this.disconnectSubject$.complete()
    clearInterval(this.idleInterval)
  }

  private getAuthMessage(): Promise<HeliosMessage<string>> {
    return this.auth.getToken().then(
      token =>
        ({
          eventType: HeliosMessageType.Authenticate,
          payload: token,
        } as HeliosMessage<string>),
    )
  }
}
